import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { signIn } from 'next-auth/react';
import getConfig from 'next/config';
import { useController, useFormContext } from 'react-hook-form';
import { isEmpty } from 'lodash';
import {
    Fade,
    FormControl,
    FormControlLabel,
    FormLabel,
    RadioGroup
} from '@material-ui/core';
import {
    Alert,
    Box,
    Button,
    Grid,
    Radio,
    Skeleton,
    TextField,
    Typography
} from '@nfs/ui-kit';

import InlineError from '@/components/inline-error';
import Link from '@/components/link';
import { ACCESS_POLICY, FILE_A_CLAIM, PAY_RENEWAL } from '@/lib/constants';

import styles from './styles.module.scss';

const { publicRuntimeConfig } = getConfig() ?? {};
const { NEXT_PUBLIC_MY_FLOOD_LINK } = publicRuntimeConfig ?? {};

const SELECTED_LABEL = {
    [ACCESS_POLICY]: 'Save & Continue',
    [FILE_A_CLAIM]: 'Continue with Claim',
    [PAY_RENEWAL]: 'Review Premium'
};

/**
 * Welcome to Flood Portal component.
 *
 * @param {*} props
 * @returns
 */
const WelcomeToFloodPortal = props => {
    const {
        authenticated,
        loading,
        noAccess,
        noPolicy,
        noRenewal,
        notEligibleRenewal,
        notEligibleRollover,
        onReset,
        selected,
        submitting,
        title
    } = props;

    const likeToDoDefaultValue = selected;

    const SELECTED_COPY = {
        [ACCESS_POLICY]: authenticated
            ? 'Please enter the following information, as listed on your policy declaration page to link your policy.'
            : "You'll need to create an account or sign in if you already have one.",
        [FILE_A_CLAIM]:
            'Please enter the following information as it is listed on your policy declaration page or related document.',
        [PAY_RENEWAL]:
            'Please enter the following information as it is listed on your renewal notice.'
    };

    const {
        control,
        formState: { errors, isSubmitted, isValidating },
        getValues,
        reset
    } = useFormContext();

    const { field: policyNumber } = useController({
        control,
        name: 'policyNumber'
    });

    const { field: name } = useController({
        control,
        name: 'name'
    });

    const { field: zipCode } = useController({
        control,
        name: 'zipCode'
    });

    const { field: likeToDo } = useController({
        control,
        name: 'likeToDo'
    });

    const likeToDoCurrentValue = getValues('likeToDo') ?? selected;

    useEffect(() => {
        if (isSubmitted && isValidating) {
            onReset();
        }
    }, [isSubmitted, isValidating, onReset]);

    useEffect(() => {
        reset(
            {
                likeToDo: likeToDoCurrentValue
            },
            {
                keepValues: true
            }
        );
    }, [likeToDoCurrentValue, reset, getValues]);

    const handleSignIn = () => {
        const callbackUrl = new URL(window.location.href);
        callbackUrl.pathname = '/account';

        signIn(null, {
            callbackUrl: callbackUrl.href
        });
    };

    return (
        <Grid container spacing={4} className={styles.root}>
            {title && (
                <Grid item xs={12}>
                    <Typography component="h1">
                        {loading && <Skeleton height={48} />}
                        {!loading && title}
                    </Typography>
                </Grid>
            )}
            <Grid item xs={12}>
                {loading && (
                    <>
                        <Skeleton height={24} width={200} />
                        <Skeleton height={24} />
                    </>
                )}
                {!loading && (
                    <FormControl component="fieldset">
                        <RadioGroup
                            aria-label="What would you like to do?"
                            defaultValue={likeToDoDefaultValue}
                        >
                            <Grid container spacing={1}>
                                <Grid item xs={12}>
                                    <FormLabel>
                                        <Typography>
                                            What would you like to do?
                                        </Typography>
                                    </FormLabel>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                {...likeToDo}
                                                value={PAY_RENEWAL}
                                            />
                                        }
                                        label="Pay Renewal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                {...likeToDo}
                                                value={FILE_A_CLAIM}
                                            />
                                        }
                                        label="File a Claim"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                {...likeToDo}
                                                value={ACCESS_POLICY}
                                            />
                                        }
                                        label="Access your Policy Declaration Page"
                                    />
                                </Grid>
                            </Grid>
                        </RadioGroup>
                    </FormControl>
                )}
            </Grid>
            <Grid item xs={12}>
                <Typography>
                    {loading && (
                        <>
                            <Skeleton height={24} />
                            <Skeleton height={24} />
                            <Skeleton height={24} width="50%" />
                        </>
                    )}

                    {!loading && SELECTED_COPY[likeToDoCurrentValue]}
                </Typography>
            </Grid>
            {notEligibleRenewal && (
                <Fade in={notEligibleRenewal}>
                    <Grid item xs={12}>
                        <Alert severity="error">
                            The policy is no longer eligible for renewal, please
                            contact your agent.
                        </Alert>
                    </Grid>
                </Fade>
            )}
            {notEligibleRollover && (
                <Fade in={notEligibleRollover}>
                    <Grid item xs={12}>
                        <Alert severity="error">
                            The policy is no longer eligible for rollover,
                            please contact your agent.
                        </Alert>
                    </Grid>
                </Fade>
            )}
            {noAccess && (
                <Fade in={noAccess}>
                    <Grid item xs={12}>
                        <Alert severity="error">
                            We were unable to access this policy. This policy
                            may already be associated with another account.
                        </Alert>
                    </Grid>
                </Fade>
            )}
            {noPolicy && (
                <Fade in={noPolicy}>
                    <Grid item xs={12}>
                        <Alert severity="error">
                            We were unable to find a policy with this
                            information. Please try again or contact your agent.
                        </Alert>
                    </Grid>
                </Fade>
            )}
            {noRenewal && (
                <Fade in={noRenewal}>
                    <Grid item xs={12}>
                        <Alert severity="info">
                            There is no renewal payment due for your policy at
                            this time. Please contact your agent if you have any
                            questions.
                        </Alert>
                    </Grid>
                </Fade>
            )}
            {!isEmpty(errors) && (
                <Fade in={!isEmpty(errors)}>
                    <Grid item xs={12}>
                        <Alert severity="error">
                            Please correct the following{' '}
                            {Object.keys(errors).length === 1
                                ? 'error'
                                : 'errors'}
                            .
                        </Alert>
                    </Grid>
                </Fade>
            )}
            {([PAY_RENEWAL, FILE_A_CLAIM].includes(likeToDoCurrentValue) ||
                authenticated) && (
                <>
                    <Grid item xs={12}>
                        {loading && (
                            <>
                                <Skeleton height={24} width={200} />
                                <Skeleton height={40} />
                            </>
                        )}
                        {!loading && (
                            <TextField
                                {...policyNumber}
                                autoFocus
                                error={Boolean(errors?.policyNumber)}
                                id="policyNumber"
                                label="Policy Number (14-digits)"
                                maxLength={14}
                                type="text"
                                variant="outlined"
                                inputProps={{
                                    autoComplete: 'off'
                                }}
                                helperText={
                                    errors.policyNumber && (
                                        <InlineError
                                            message={
                                                errors.policyNumber.message
                                            }
                                        />
                                    )
                                }
                            />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        {loading && (
                            <>
                                <Skeleton height={24} width={200} />
                                <Skeleton height={40} />
                            </>
                        )}
                        {!loading && (
                            <TextField
                                {...name}
                                error={Boolean(errors?.name)}
                                id="name"
                                label="Policy Holder Last Name or Business Name"
                                type="text"
                                variant="outlined"
                                inputProps={{
                                    autoComplete: 'off'
                                }}
                                helperText={
                                    errors.name && (
                                        <InlineError
                                            message={errors.name.message}
                                        />
                                    )
                                }
                            />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        {loading && (
                            <>
                                <Skeleton height={24} width={200} />
                                <Skeleton height={40} />
                            </>
                        )}
                        {!loading && (
                            <TextField
                                {...zipCode}
                                error={Boolean(errors?.zipCode)}
                                id="zipCode"
                                label="Property Zip Code"
                                maxLength={5}
                                type="text"
                                variant="outlined"
                                inputProps={{
                                    autoComplete: 'off'
                                }}
                                helperText={
                                    errors.zipCode && (
                                        <InlineError
                                            message={errors.zipCode.message}
                                        />
                                    )
                                }
                            />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <Box mt={2}>
                            {loading && <Skeleton height={60} />}
                            {!loading && (
                                <Button
                                    fullWidth
                                    color="primary"
                                    data-testid="continue-button"
                                    disabled={submitting}
                                    loading={submitting}
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                >
                                    {SELECTED_LABEL[likeToDoCurrentValue]}
                                </Button>
                            )}
                        </Box>
                    </Grid>
                </>
            )}
            {likeToDoCurrentValue === ACCESS_POLICY && !authenticated && (
                <>
                    <Grid item xs={12}>
                        <Box mt={2}>
                            <Button
                                component={Link}
                                fullWidth
                                color="secondary"
                                size="large"
                                variant="contained"
                                href="/create-account"
                                underline="none"
                            >
                                Create an Account
                            </Button>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box
                            mt={2}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Button
                                color="primary"
                                onClick={handleSignIn}
                                variant="text"
                            >
                                <Typography align="center" component="span">
                                    Sign in
                                </Typography>
                            </Button>
                        </Box>
                    </Grid>
                </>
            )}
        </Grid>
    );
};

WelcomeToFloodPortal.propTypes = {
    authenticated: PropTypes.bool,
    loading: PropTypes.bool,
    noAccess: PropTypes.bool,
    noPolicy: PropTypes.bool,
    noRenewal: PropTypes.bool,
    notEligibleRenewal: PropTypes.bool,
    notEligibleRollover: PropTypes.bool,
    onReset: PropTypes.func,
    selected: PropTypes.string,
    submitting: PropTypes.bool,
    title: PropTypes.string
};

WelcomeToFloodPortal.defaultProps = {
    authenticated: false,
    loading: false,
    noAccess: false,
    noPolicy: false,
    noRenewal: false,
    notEligibleRenewal: false,
    notEligibleRollover: false,
    onReset: () => {},
    selected: '',
    submitting: false,
    title: ''
};

export default WelcomeToFloodPortal;
